import { Input, Popover, Tooltip } from 'antd';
import { useContext, useMemo, useState } from 'react';
import { ProductPPG, Metric } from 'types/tradePromoOptimization';
import { TPODataContext } from 'contexts/TPOContext';
import { InfoCircleOutlined } from '@ant-design/icons';
import { colorConstants } from 'config/colorConstants';
import KPISelector, { KPIItem } from './KPISelector';
import {
    checkPromoStatus,
    handleMetricInputChange,
    handleNumberInputKeyDown,
    isValidMetricInput,
} from '../../tpoHelperMethods';
import _ from 'lodash';
import MetricCell from './MetricSection/MetricCell';
import TotalsCell from './MetricSection/TotalsCell';
import { getEditButtonState } from 'utils/helperMethods';
import { tpoPromoMetricKeys } from 'utils/Enums';

const PromoMetrics = ({ promoSyncId, showTotals }: { promoSyncId: string; showTotals: boolean }) => {
    const {
        updateSelectedEventProperty,
        selectedEventInfo,
        viewMode,
        isCurrentPromoFormDisabled,
        isTpmTab,
        promoConfigDictionary,
        skipSRPUpdateRef,
        tempEventProperties,
        finalCalenderStatues,
    } = useContext(TPODataContext);

    const [editingCell, setEditingCell] = useState<{ metricId: number; ppgName: string } | null>(null);
    const [editValue, setEditValue] = useState('');
    const combinedViewMode = viewMode || isTpmTab || isCurrentPromoFormDisabled;

    const currentPromo = selectedEventInfo?.currentPromoTactic;
    const planStatus = finalCalenderStatues?.approval_status?.toLowerCase();
    const promoStatus = selectedEventInfo?.currentPromoTactic?.status?.toLowerCase();

    const isInputDisabled = (metricId: number, skipEditableCheck = false) => {
        const item =
            promoConfigDictionary.promoMetricMapping?.[Number(currentPromo?.promoType)]?.promoMetrics?.[metricId];

        const isPastDatesEditable = item?.is_past_dates_editable;
        const isEditable = item?.editable;
        const metricNameKey = item?.name_key;

        const isSpecialMetric =
            metricNameKey === tpoPromoMetricKeys.SRP_TRUE || metricNameKey === tpoPromoMetricKeys.PROMOTED_SRP;
        const getStatus = checkPromoStatus(promoStatus as string);
        if (getStatus) {
            return true;
        }
        if (isSpecialMetric) {
            return !isEditable || !isPastDatesEditable;
        }

        const editButtonDisabledState = getEditButtonState(
            planStatus,
            selectedEventInfo,
            tempEventProperties?.CRAStartDate,
        )?.isDisabled;

        // For list items, skip the editable check
        if (skipEditableCheck) {
            return editButtonDisabledState;
        }

        return !isEditable || editButtonDisabledState;
    };

    const metricKPIs: KPIItem[] = useMemo(() => {
        const kpis =
            currentPromo?.ppgs[0]?.metrics.map((m: Metric) => ({
                value: m.id?.toString(),
                label: m.name,
                isSelected: m.isSelected,
                isHidden: m.isHidden,
            })) || [];

        const orderMap: Record<number, number> = Object.entries(promoConfigDictionary?.promoMetrics || {}).reduce(
            (acc, [key, value]) => {
                acc[value.id] = value.orderId ?? 0;
                return acc;
            },
            {} as Record<number, number>,
        );
        return kpis.sort((a, b) => {
            const orderA = orderMap[Number(a.value)] ?? 0;
            const orderB = orderMap[Number(b.value)] ?? 0;
            return orderA - orderB;
        });
    }, [currentPromo?.ppgs, promoConfigDictionary?.promoMetrics]);

    const metricKPIsForSelector: KPIItem[] = useMemo(() => {
        if (currentPromo?.promoType) {
            const avlMetrics = promoConfigDictionary?.promoMetricMapping[currentPromo.promoType]?.promoMetrics;
            const kpis =
                currentPromo?.ppgs[0]?.metrics.map((m: Metric) => ({
                    value: m.id?.toString(),
                    label: m.name,
                    isSelected: m.isSelected,
                    groupName: avlMetrics[m.id]?.groupName || '',
                    isHidden: m.isHidden,
                    isDisabled: isInputDisabled(m.id, true),
                })) || [];
            const allGroups = Array.from(new Set(kpis.map((kpi) => kpi.groupName)));

            return allGroups.sort().map((groupName) => {
                const groupMetrics = kpis.filter((kpi) => kpi.groupName === groupName);
                const isGroupDisabled = groupMetrics.length > 0 && groupMetrics.every((kpi) => kpi.isDisabled);

                return {
                    value: groupName,
                    label: groupName,
                    isSelected: kpis.some((kpi) => kpi.groupName === groupName && kpi.isSelected),
                    isHidden: kpis.some((kpi) => kpi.groupName === groupName && kpi.isHidden),
                    isDisabled: isGroupDisabled,
                };
            });
        }
        return [];
    }, [metricKPIs, promoConfigDictionary, currentPromo?.promoType]);

    const handleInputChange = (metricId: number, productName: string, value: string) => {
        if (!selectedEventInfo?.currentPromoTactic) return;

        const updatedPPGs: any = handleMetricInputChange(
            promoConfigDictionary,
            selectedEventInfo,
            metricId,
            productName,
            value,
            true,
        );

        if (updatedPPGs)
            // Save changes
            updateSelectedEventProperty('currentPromoTactic', {
                ...selectedEventInfo?.currentPromoTactic,
                ppgs: updatedPPGs,
                hasChanges: true,
            });
    };

    const handleTotalInputChange = (metricId: number, productName: string, value: string) => {
        // todo: not required for promo metric, make generic code change
        return;
    };

    const [isPopoverVisible, setIsPopoverVisible] = useState(false);

    const onMetricKPISelectHandler = (updatedGroups: string[]) => {
        // avoid setting srp value
        skipSRPUpdateRef.current = true;

        // Guard against undefined
        if (!selectedEventInfo?.currentPromoTactic) return;

        // `updatedGroups` contains selected groupNames
        const avlMetrics = promoConfigDictionary?.promoMetricMapping[currentPromo?.promoType || 1]?.promoMetrics;
        selectedEventInfo?.currentPromoTactic.ppgs.forEach((ppg: ProductPPG) => {
            ppg.metrics.forEach((metric: Metric) => {
                metric.isSelected = updatedGroups.includes(avlMetrics[metric.id].groupName);
            });
        });

        // enable save button on change of kpis
        if (!viewMode && selectedEventInfo) {
            selectedEventInfo.currentPromoTactic.hasChanges = true;
        }

        if (selectedEventInfo?.currentPromoTactic) {
            updateSelectedEventProperty('currentPromoTactic', _.cloneDeep(selectedEventInfo.currentPromoTactic));
        }
    };

    return (
        <>
            <div className=" flex">
                <div className="flex flex-col min-w-[260px]  max-w-[260px] ">
                    {metricKPIs
                        ?.filter((m: KPIItem) => m.isSelected && !m.isHidden)
                        ?.map((metric: KPIItem, index: number) => {
                            return (
                                <Tooltip
                                    placement="top"
                                    color="white"
                                    destroyTooltipOnHide
                                    overlayStyle={{ borderRadius: 4 }}
                                    overlayInnerStyle={{
                                        color: colorConstants.darkGrey,
                                    }}
                                    title={metric.label}
                                >
                                    <div
                                        className={`block truncate h-9 border-l border-r border-b p-2 font-xs border-backgroundGreyAlt1 bg-white
                                        ${isInputDisabled(Number(metric.value)) ? 'text-backgroundGreyAlt2' : ''}
                                        `}
                                    >
                                        {metric.label}
                                    </div>
                                </Tooltip>
                            );
                        })}
                </div>

                <div className={`flex flex-grow overflow-x-scroll  hide-scrollable ${promoSyncId} `}>
                    {showTotals && currentPromo?.ppgs && (
                        <TotalsCell
                            key={1}
                            ppgs={currentPromo?.ppgs}
                            headerKPIs={metricKPIs}
                            promoConfigDictionary={promoConfigDictionary}
                            isInputDisabled={isInputDisabled}
                            combinedViewMode={combinedViewMode}
                            editingCell={editingCell}
                            setEditingCell={setEditingCell}
                            editValue={editValue}
                            setEditValue={setEditValue}
                            isValidMetricInput={isValidMetricInput}
                            handleInputChange={handleTotalInputChange}
                            metricType="metrics"
                        />
                    )}
                    {currentPromo?.ppgs
                        ?.filter((pm: ProductPPG) => pm.isSelected)
                        ?.map((pm: ProductPPG) => {
                            return (
                                <MetricCell
                                    key={pm.id}
                                    pm={pm}
                                    headerKPIs={metricKPIs}
                                    promoConfigDictionary={promoConfigDictionary}
                                    isInputDisabled={isInputDisabled}
                                    combinedViewMode={combinedViewMode}
                                    editingCell={editingCell}
                                    setEditingCell={setEditingCell}
                                    editValue={editValue}
                                    setEditValue={setEditValue}
                                    isValidMetricInput={isValidMetricInput}
                                    handleInputChange={handleInputChange}
                                    metricType="metrics"
                                />
                            );
                        })}
                </div>
            </div>
            <div className="bg-backgroundGreyAlt  px-2 py-1 relative w-full">
                <div className="inline-flex justify-start items-center gap-4 cursor-pointer">
                    <KPISelector
                        isVisible={isPopoverVisible}
                        setIsVisible={setIsPopoverVisible}
                        kpiList={metricKPIsForSelector.filter((item) => !item.isHidden) || []}
                        onSubmit={onMetricKPISelectHandler}
                        buttonName={'Add / Remove Metrics'}
                        searchPlaceholder="Search KPI..."
                        variableName="Metric"
                        disableMinimumOneSelectionCondition
                        addDisabled={checkPromoStatus(promoStatus as string)}
                    />
                </div>
            </div>
        </>
    );
};

export default PromoMetrics;
